/*
 * Page collection et recherche : mêmes éléments personnalisés que lancaster.com
 * (sticky-facet-filters, facet-drawer, facet-filters-form, facet-remove, price-range, infinite-scroll, view-more-button).
 * Notre propre code : les filtres s'appliquent en direct (page rechargée en arrière-plan, grille et compteurs remplacés),
 * « Afficher plus » ajoute la suite sans bouger la page.
 */
(() => {
  if (window.__lcCollection) return;
  window.__lcCollection = true;

  const DRAWER_MS = 500;
  let renderToken = 0;
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Fondus d'images (is="lazy-image") ---------- */
  function imgDone(img) {
    img.classList.add('loaded');
    const wrap = img.closest('.loading');
    if (wrap) wrap.classList.remove('loading');
  }
  function watchImg(img) {
    if (img.__lcWatched) return;
    img.__lcWatched = true;
    if (img.complete && img.naturalWidth) { imgDone(img); return; }
    img.addEventListener('load', () => imgDone(img));
    img.addEventListener('error', () => imgDone(img));
  }
  function scanImages(root) {
    (root || document).querySelectorAll('img[is="lazy-image"]').forEach(watchImg);
  }

  /* ---------- use-animate (apparitions au scroll) ---------- */
  const seen = (!reduceMotion && 'IntersectionObserver' in window) ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.setAttribute('animate', '');
      seen.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -50px 0px', threshold: 0.05 }) : null;

  class UseAnimate extends HTMLElement {
    connectedCallback() {
      if (reduceMotion || !seen) {
        this.setAttribute('animate', '');
      } else {
        seen.observe(this);
      }
    }
  }

  /* ---------- Barre : se masque en descendant, revient en remontant ---------- */
  class StickyFacetFilters extends HTMLElement {
    connectedCallback() {
      this.lastScrollTop = 0;
      this.classList.add('facet-filters-static');
      this.handler = () => this.onScroll();
      window.addEventListener('scroll', this.handler, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handler);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (document.body.classList.contains('facet-drawer--open')) return;
      if (scrollTop > this.parentNode.offsetTop) {
        window.requestAnimationFrame(() => this.classList.add('shopify-section-filters-sticky'));
        const delta = scrollTop - this.lastScrollTop;
        if (delta > 4) window.requestAnimationFrame(() => this.classList.add('filters-scroll-hidden'));
        else if (delta < -4) window.requestAnimationFrame(() => this.classList.remove('filters-scroll-hidden'));
      } else {
        window.requestAnimationFrame(() => {
          this.classList.remove('shopify-section-filters-sticky', 'filters-scroll-hidden');
        });
      }
      this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
    }
  }

  /* ---------- Tiroir « Filtrer et trier » ---------- */
  class FacetDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details && this.details.querySelector(':scope > summary');
      if (!this.summary) return;
      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.details.hasAttribute('open') && this.details.classList.contains('menu-opening')) this.close();
        else if (!this.details.hasAttribute('open')) this.open();
      });
      this.summary.addEventListener('keydown', (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target.closest('.mobile-facets__close')) {
          event.preventDefault();
          this.close();
        }
      });
      this.querySelectorAll('[data-facet-apply]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.keyHandler = (event) => {
        if (event.key === 'Escape' && this.details.classList.contains('menu-opening')) this.close();
      };
      document.addEventListener('keydown', this.keyHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.keyHandler);
    }

    open() {
      const sticky = this.closest('sticky-facet-filters');
      if (sticky) sticky.classList.remove('filters-scroll-hidden');
      this.details.setAttribute('open', '');
      document.body.classList.add('facet-drawer--open');
      // le tiroir doit être peint fermé avant de glisser
      void this.details.offsetWidth;
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => this.details.classList.add('menu-opening')));
      this.summary.setAttribute('aria-expanded', 'true');
      const close = this.querySelector('.mobile-facets__close');
      if (close) setTimeout(() => close.focus({ preventScroll: true }), 50);
    }

    close() {
      this.details.classList.remove('menu-opening');
      this.summary.setAttribute('aria-expanded', 'false');
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        this.details.removeAttribute('open');
        document.body.classList.remove('facet-drawer--open');
        this.summary.focus({ preventScroll: true });
      }, DRAWER_MS);
    }
  }

  /* ---------- Filtres en direct ---------- */
  const bar = () => document.getElementById('main-facet-filters');

  const buildUrl = (origin) => {
    const root = bar();
    if (!root) return null;
    const mobile = root.querySelector('#FacetFiltersFormMobile');
    const desktop = root.querySelector('#FacetSortFiltersForm');
    const params = new URLSearchParams();
    const source = mobile || desktop;
    if (source) {
      new FormData(source).forEach((value, key) => {
        if (key === 'sort_by' || value === '') return;
        params.append(key, value);
      });
    }
    // le tri n'est ajouté que s'il vient d'être choisi ou s'il était déjà dans l'adresse :
    // sinon l'ordre par défaut de la collection changerait en filtrant
    const fromSortControl = origin && (origin.name === 'sort_by' || origin.id === 'SortBy-mobile');
    const sort = fromSortControl ? origin.value : new URLSearchParams(window.location.search).get('sort_by');
    if (sort) params.set('sort_by', sort);
    let action = (mobile || desktop).getAttribute('action') || window.location.pathname;
    // filtres par étiquettes (collection) : Shopify les attend dans le chemin, /collection/<étiquette1>+<étiquette2>
    const tags = Array.from(root.querySelectorAll('input[data-tag-handle]:checked')).map((input) => input.dataset.tagHandle);
    if (tags.length) action = `${action.replace(/\/$/, '')}/${tags.join('+')}`;
    const query = params.toString();
    return query ? `${action}?${query}` : action;
  };

  const syncInputs = (doc, skip) => {
    const root = bar();
    if (!root) return;
    root.querySelectorAll('input[id], select[id]').forEach((el) => {
      if (el === skip) return;
      const fresh = doc.getElementById(el.id);
      if (!fresh) return;
      if (el.type === 'checkbox' || el.type === 'radio') {
        el.checked = fresh.checked;
        el.disabled = fresh.disabled;
        if (fresh.dataset.count !== undefined) el.dataset.count = fresh.dataset.count;
        const label = el.closest('label');
        if (label) label.classList.toggle('mobile-facets__label--disabled', fresh.disabled);
      } else if (el.tagName === 'SELECT') {
        el.value = fresh.value;
      } else if (el.type === 'number') {
        el.value = fresh.value;
        el.placeholder = fresh.placeholder;
        el.max = fresh.max;
      }
    });
    root.querySelectorAll('price-range').forEach((range) => range.refresh && range.refresh());
    // pastille « nombre de filtres actifs » de chaque groupe
    root.querySelectorAll('details.mobile-facets__details[data-index]').forEach((details) => {
      const fresh = doc.querySelector('details.mobile-facets__details[data-index="' + details.dataset.index + '"]');
      const title = details.querySelector('.facets__selected > span:first-child');
      if (!fresh || !title) return;
      const next = fresh.querySelector('.facets__selected > .count-bubble');
      const mine = details.querySelector('.facets__selected > .count-bubble');
      if (!next && mine) mine.remove();
      else if (next && mine) mine.textContent = next.textContent;
      else if (next) title.insertAdjacentHTML('afterend', next.outerHTML);
    });
  };

  const render = async (url, origin) => {
    const token = ++renderToken;
    const container = document.getElementById('ProductGridContainer');
    const collection = container && container.querySelector('.collection');
    const counts = document.querySelectorAll('.product-count__text');
    if (collection) collection.classList.add('loading');
    counts.forEach((el) => el.classList.add('loading'));
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));
      const doc = parse(await response.text());
      if (token !== renderToken) return;
      const freshContainer = doc.getElementById('ProductGridContainer');
      if (freshContainer && container) {
        container.innerHTML = freshContainer.innerHTML;
        scanImages(container);
      }
      ['ProductCount', 'ProductCountMobile'].forEach((id) => {
        const mine = document.getElementById(id);
        const fresh = doc.getElementById(id);
        if (mine && fresh) mine.textContent = fresh.textContent;
      });
      const mineActive = document.querySelector('.active-facets-mobile .active-facets');
      const freshActive = doc.querySelector('.active-facets-mobile .active-facets');
      if (mineActive && freshActive) mineActive.outerHTML = freshActive.outerHTML;
      const mineOpen = document.querySelector('.mobile-facets__open');
      const freshOpen = doc.querySelector('.mobile-facets__open');
      if (mineOpen && freshOpen) mineOpen.innerHTML = freshOpen.innerHTML;
      const mineClear = document.querySelector('.mobile-facets__clear');
      const freshClear = doc.querySelector('.mobile-facets__clear');
      if (mineClear && freshClear) mineClear.setAttribute('href', freshClear.getAttribute('href'));
      syncInputs(doc, origin);
      window.history.replaceState({}, '', url);
    } catch (error) {
      window.location.href = url;
    } finally {
      if (token === renderToken) {
        const now = document.querySelector('#ProductGridContainer .collection');
        if (now) now.classList.remove('loading');
        document.querySelectorAll('.product-count__text').forEach((el) => el.classList.remove('loading'));
      }
    }
  };

  class FacetFiltersForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply(null);
      });
      let timer;
      this.form.addEventListener('change', (event) => {
        const target = event.target;
        const sort = target.closest('details.facet-filters__sort');
        if (sort) sort.removeAttribute('open');
        clearTimeout(timer);
        timer = setTimeout(() => this.apply(target), 250);
      });
    }

    apply(origin) {
      const url = buildUrl(origin);
      if (url) render(url, origin);
    }
  }

  class FacetRemove extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        if (!link || event.metaKey || event.ctrlKey) return;
        event.preventDefault();
        const url = link.getAttribute('href');
        if (url) render(url, null);
      });
    }
  }

  /* ---------- Prix : deux poignées et deux champs ---------- */
  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.querySelector('.price-range__track-wrapper');
      this.lower = this.querySelector('.price-range__thumbs.is-lower');
      this.upper = this.querySelector('.price-range__thumbs.is-upper');
      const inputs = this.querySelectorAll('.field__input');
      this.minInput = inputs[0];
      this.maxInput = inputs[1];
      const outputs = this.querySelectorAll('.price-range__output-text .money');
      this.minOut = outputs[0];
      this.maxOut = outputs[1];
      if (!this.minInput || !this.maxInput) return;
      this.refresh();
      [this.minInput, this.maxInput].forEach((input) => input.addEventListener('input', () => this.refresh()));
      [this.lower, this.upper].forEach((thumb) => {
        if (!thumb) return;
        thumb.addEventListener('pointerdown', (event) => this.dragStart(event, thumb));
        thumb.addEventListener('keydown', (event) => this.key(event, thumb));
      });
    }

    get bounds() {
      return { min: Number(this.dataset.min) || 0, max: Number(this.dataset.max) || 0 };
    }

    values() {
      const { min, max } = this.bounds;
      let low = this.minInput.value === '' ? min : Number(this.minInput.value);
      let high = this.maxInput.value === '' ? max : Number(this.maxInput.value);
      low = Math.min(Math.max(low, min), max);
      high = Math.min(Math.max(high, min), max);
      if (low > high) [low, high] = [high, low];
      return { low, high };
    }

    refresh() {
      if (!this.minInput) return;
      const { min, max } = this.bounds;
      const span = max - min || 1;
      const { low, high } = this.values();
      this.style.setProperty('--progress-lower', `${((low - min) * 100) / span}%`);
      this.style.setProperty('--progress-upper', `${((high - min) * 100) / span}%`);
      const line = this.nextElementSibling;
      if (line) {
        const sums = line.querySelectorAll('.money');
        const symbol = (this.querySelector('.price-range__prefix') || {}).textContent;
        const unit = ` ${(symbol || '€').trim()}`;
        if (sums[0]) sums[0].textContent = `${Math.round(low)}${unit}`;
        if (sums[1]) sums[1].textContent = `${Math.round(high)}${unit}`;
      }
      if (this.minOut) this.minOut.textContent = String(Math.round(low));
      if (this.maxOut) this.maxOut.textContent = String(Math.round(high));
      if (this.lower) this.lower.setAttribute('aria-valuenow', String(Math.round(low)));
      if (this.upper) this.upper.setAttribute('aria-valuenow', String(Math.round(high)));
    }

    set(which, value) {
      const { min, max } = this.bounds;
      const { low, high } = this.values();
      const next = Math.round(Math.min(Math.max(value, min), max));
      if (which === 'lower') {
        const v = Math.min(next, high);
        this.minInput.value = v <= min ? '' : String(v);
      } else {
        const v = Math.max(next, low);
        this.maxInput.value = v >= max ? '' : String(v);
      }
      this.refresh();
    }

    commit(input) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    dragStart(event, thumb) {
      event.preventDefault();
      const which = thumb.classList.contains('is-lower') ? 'lower' : 'upper';
      const input = which === 'lower' ? this.minInput : this.maxInput;
      const rect = this.wrapper.getBoundingClientRect();
      const { min, max } = this.bounds;
      thumb.setPointerCapture(event.pointerId);
      thumb.focus({ preventScroll: true });
      const move = (e) => {
        const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        this.set(which, min + ratio * (max - min));
      };
      const end = () => {
        thumb.removeEventListener('pointermove', move);
        thumb.removeEventListener('pointerup', end);
        thumb.removeEventListener('pointercancel', end);
        this.commit(input);
      };
      thumb.addEventListener('pointermove', move);
      thumb.addEventListener('pointerup', end);
      thumb.addEventListener('pointercancel', end);
    }

    key(event, thumb) {
      const step = event.shiftKey ? 10 : 1;
      const dir = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1 }[event.key];
      if (!dir) return;
      event.preventDefault();
      const which = thumb.classList.contains('is-lower') ? 'lower' : 'upper';
      const { low, high } = this.values();
      this.set(which, (which === 'lower' ? low : high) + dir * step);
      clearTimeout(this.keyTimer);
      this.keyTimer = setTimeout(() => this.commit(which === 'lower' ? this.minInput : this.maxInput), 400);
    }
  }

  /* ---------- « Afficher plus » ---------- */
  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', () => this.load());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.load();
        }
      });
    }

    async load() {
      if (this.classList.contains('loading') || this.classList.contains('disabled')) return;
      this.classList.add('loading', 'disabled');
      try {
        const response = await fetch(this.dataset.url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(String(response.status));
        const doc = parse(await response.text());
        const grid = document.getElementById('product-grid');
        const fresh = doc.getElementById('product-grid');
        if (grid && fresh) {
          const freshItems = Array.from(fresh.children);
          freshItems.forEach((child) => child.classList.add('product-appear'));
          grid.append(...freshItems);
          scanImages(grid);
        }
        const wrapper = this.closest('.pagination-wrapper');
        const next = doc.querySelector('#ProductGridContainer .pagination-wrapper');
        if (wrapper) {
          if (next) wrapper.innerHTML = next.innerHTML;
          else wrapper.remove();
        }
      } catch (error) {
        window.location.href = this.dataset.url;
      } finally {
        this.classList.remove('loading', 'disabled');
      }
    }
  }

  /* ---------- Description : afficher plus / moins ---------- */
  class ViewMoreButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      const target = document.getElementById(this.getAttribute('aria-controls'));
      if (!this.button || !target) return;
      this.labelMore = this.button.textContent.trim();
      this.button.addEventListener('click', () => {
        const open = target.getAttribute('aria-expanded') === 'true';
        target.setAttribute('aria-expanded', String(!open));
        this.button.textContent = open ? this.labelMore : this.button.dataset.textLess;
      });
    }
  }

  /* ---------- Menu de tri : se ferme au clic ailleurs et à Échap ---------- */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details.facet-filters__sort[open]').forEach((sort) => {
      if (!sort.contains(event.target)) sort.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll('details.facet-filters__sort[open]').forEach((sort) => sort.removeAttribute('open'));
    }
  });

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  define('use-animate', UseAnimate);
  define('sticky-facet-filters', StickyFacetFilters);
  define('facet-drawer', FacetDrawer);
  define('facet-filters-form', FacetFiltersForm);
  define('facet-remove', FacetRemove);
  define('price-range', PriceRange);
  define('infinite-scroll', InfiniteScroll);
  define('view-more-button', ViewMoreButton);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scanImages());
  } else {
    scanImages();
  }
})();
